import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, User, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-562">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-563">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-564">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-565">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-566" /> : <Plus className="w-4 h-4" data-id="element-567" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-568">
        <p className="text-gray-600 leading-relaxed" data-id="element-569">{answer}</p>
      </div>
    </div>;
}
export function ImplantyCostArticle() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Чи покриває NHS імплантацію зубів?',
    a: 'Зазвичай ні. NHS покриває імпланти лише у виняткових медичних випадках (наприклад, після травми обличчя або онкології). У більшості випадків це виключно приватна процедура.'
  }, {
    q: 'Що входить у вартість "під ключ"?',
    a: 'Повна вартість зазвичай включає: консультацію, хірургічний етап (встановлення імпланта), формувач ясен, абатмент та коронку. Додатково можуть оплачуватися КТ-знімок та кісткова пластика.'
  }, {
    q: 'Чи є приховані платежі?',
    a: 'У Dentico Clinic ми надаємо повний кошторис до початку лікування. Ви будете знати точну суму за кожен етап.'
  }, {
    q: 'Чи можна оплатити частинами?',
    a: 'Так, ми пропонуємо плани розстрочки (finance options), щоб зробити лікування доступнішим. Обговоріть це з адміністратором.'
  }, {
    q: 'Чому ціни в різних клініках так відрізняються?',
    a: 'Ціна залежить від бренду імпланта (Straumann, Nobel vs бюджетні), кваліфікації хірурга, локації клініки та рівня сервісу.'
  }, {
    q: 'Скільки служить імплант?',
    a: 'Якісні імпланти можуть служити все життя при правильному догляді. Коронки зазвичай потребують заміни через 10-15 років.'
  }];
  // Schemas
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Скільки коштують імпланти в Лондоні? Повний гід по цінах 2025',
    datePublished: '2026-02-12',
    author: {
      '@type': 'Organization',
      name: 'Dentico Clinic'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dentico Clinic',
      logo: {
        '@type': 'ImageObject',
        url: 'https://denticoclinic.co.uk/logo.png'
      }
    },
    description: 'Дізнайтеся актуальні ціни на зубні імпланти в Лондоні, що входить у вартість та як уникнути переплат. Гід від Dentico Clinic.',
    inLanguage: 'uk'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-570">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-571" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-572" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-573" />

      <article className="bg-white" data-id="element-574">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-575">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-576">
            <Link to="/ua/blog" className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-577">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-578" />
              Назад до блогу
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-579">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-580">
                <Tag className="w-3 h-3 mr-2" data-id="element-581" />
                Імплантація
              </span>
              <span className="flex items-center" data-id="element-582">
                <Calendar className="w-4 h-4 mr-2" data-id="element-583" />
                Лютий 2026
              </span>
              <span className="flex items-center" data-id="element-584">
                <Clock className="w-4 h-4 mr-2" data-id="element-585" />8 хв читання
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-586">
              Скільки коштують імпланти в Лондоні? Повний гід по цінах 2025
            </h1>

            <div className="flex items-center" data-id="element-587">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-588">
                DC
              </div>
              <div data-id="element-589">
                <p className="font-medium text-gray-900" data-id="element-590">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-591">Експертна стаття</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-592">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-593">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-594">
              Втрата зуба — це не лише естетична проблема, а й питання здоров'я.
              Імплантація є найкращим рішенням, але багатьох пацієнтів лякає
              невідомість щодо вартості. У цій статті ми розберемо, скільки
              реально коштують імпланти в Лондоні, що впливає на ціну та як
              уникнути прихованих витрат.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-595">
              Чому ціни на імпланти так відрізняються?
            </h2>
            <p className="mb-6" data-id="element-596">
              Якщо ви шукали ціни на імплантацію в Лондоні, то могли помітити
              величезний діапазон: від £1,000 до £4,000 за один зуб. Чому така
              різниця?
            </p>
            <ul className="space-y-3 mb-8" data-id="element-597">
              <li className="flex items-start" data-id="element-598">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-599" />
                <span data-id="element-600">
                  <strong data-id="element-601">Бренд імпланта:</strong> Преміум-системи (Straumann,
                  Nobel Biocare) коштують дорожче, але мають десятиліття
                  досліджень та довічну гарантію. Бюджетні варіанти дешевші, але
                  можуть мати менш передбачуваний результат.
                </span>
              </li>
              <li className="flex items-start" data-id="element-602">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-603" />
                <span data-id="element-604">
                  <strong data-id="element-605">Кваліфікація лікаря:</strong> Досвідчений
                  хірург-імплантолог, який постійно навчається, оцінює свою
                  роботу вище, ніж початківець.
                </span>
              </li>
              <li className="flex items-start" data-id="element-606">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-607" />
                <span data-id="element-608">
                  <strong data-id="element-609">Комплектація:</strong> Деякі клініки вказують ціну
                  лише за хірургічний етап (вкручування імпланта), не включаючи
                  абатмент і коронку. Завжди запитуйте повну вартість "під
                  ключ".
                </span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-610">
              Середні ціни на імпланти в Лондоні (2025)
            </h2>
            <p className="mb-6" data-id="element-611">
              Ось орієнтовні ціни на приватну імплантацію в Лондоні:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-100" data-id="element-612">
              <div className="grid grid-cols-1 gap-4" data-id="element-613">
                <div className="flex justify-between items-center border-b border-gray-200 pb-3" data-id="element-614">
                  <span className="font-medium text-gray-900" data-id="element-615">
                    Консультація + КТ-знімок
                  </span>
                  <span className="font-bold text-teal-700" data-id="element-616">£100 - £250</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-3" data-id="element-617">
                  <span className="font-medium text-gray-900" data-id="element-618">
                    Хірургічний етап (імплант)
                  </span>
                  <span className="font-bold text-teal-700" data-id="element-619">
                    £1,000 - £1,800
                  </span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-200 pb-3" data-id="element-620">
                  <span className="font-medium text-gray-900" data-id="element-621">
                    Абатмент + Коронка
                  </span>
                  <span className="font-bold text-teal-700" data-id="element-622">£800 - £1,500</span>
                </div>
                <div className="flex justify-between items-center pt-2" data-id="element-623">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-624">
                    Повна вартість (під ключ)
                  </span>
                  <span className="font-bold text-teal-700 text-lg" data-id="element-625">
                    £2,000 - £3,500
                  </span>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-626">
              Що може збільшити вартість?
            </h2>
            <p className="mb-6" data-id="element-627">
              У складних випадках можуть знадобитися додаткові процедури:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-628">
              <li data-id="element-629">
                <strong data-id="element-630">Кісткова пластика (Bone Grafting):</strong> Якщо кістки
                недостатньо для встановлення імпланта (£300 - £1,000).
              </li>
              <li data-id="element-631">
                <strong data-id="element-632">Синус-ліфтинг:</strong> Підняття дна гайморової пазухи
                для імплантації на верхній щелепі (£500 - £1,500).
              </li>
              <li data-id="element-633">
                <strong data-id="element-634">Видалення зуба:</strong> Якщо зуб ще не видалений (£150
                - £300).
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-635">
              NHS vs Приватна імплантація
            </h2>
            <p className="mb-6" data-id="element-636">
              Важливо розуміти:{' '}
              <strong data-id="element-637">
                NHS (державна медицина) зазвичай не покриває імплантацію зубів.
              </strong>{' '}
              Це вважається косметичною процедурою, якщо немає серйозних
              медичних показань (наприклад, вроджена відсутність зубів, наслідки
              травм обличчя або онкології).
            </p>
            <p className="mb-8" data-id="element-638">
              Тому для більшості пацієнтів імплантація доступна лише в приватних
              клініках.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-639">
              Як заощадити на імплантації?
            </h2>
            <p className="mb-6" data-id="element-640">
              1. <strong data-id="element-641">Плани розстрочки (Finance):</strong> Багато клінік,
              включаючи Dentico Clinic, пропонують можливість оплати частинами
              (0% finance).
            </p>
            <p className="mb-6" data-id="element-642">
              2. <strong data-id="element-643">Податкові пільги:</strong> У деяких випадках витрати на
              лікування можна декларувати (залежить від вашого статусу).
            </p>
            <p className="mb-8" data-id="element-644">
              3. <strong data-id="element-645">Комплексне лікування:</strong> Якщо потрібно відновити
              кілька зубів, іноді вигідніше встановити міст на імплантах, ніж
              окремий імплант для кожного зуба.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-646">
              Чому варто обрати Dentico Clinic?
            </h2>
            <p className="mb-6" data-id="element-647">
              Ми розуміємо, що імплантація — це інвестиція у ваше здоров'я. У
              Dentico Clinic ми гарантуємо:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-648">
              <li className="flex items-start" data-id="element-649">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-650" />
                <span data-id="element-651">
                  <strong data-id="element-652">Прозорість:</strong> Ви отримаєте повний план
                  лікування з фіксованою ціною до початку процедур.
                </span>
              </li>
              <li className="flex items-start" data-id="element-653">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-654" />
                <span data-id="element-655">
                  <strong data-id="element-656">Якість:</strong> Ми використовуємо лише сертифіковані
                  імпланти світових лідерів.
                </span>
              </li>
              <li className="flex items-start" data-id="element-657">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-658" />
                <span data-id="element-659">
                  <strong data-id="element-660">Мова:</strong> Консультація та супровід українською
                  мовою — ви точно зрозумієте всі деталі.
                </span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-661">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-662">
              Часті запитання про ціни
            </h3>
            <div className="space-y-2" data-id="element-663">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-664" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-665">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-666">
              Читайте також
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-667">
              <Link to="/ua/implanty-london" data-id="element-668">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-669">
                  <span className="text-teal-600 font-medium" data-id="element-670">
                    Імплантація зубів: процедура та переваги
                  </span>
                </Card>
              </Link>
              <Link to="/ua/prices" data-id="element-671">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-672">
                  <span className="text-teal-600 font-medium" data-id="element-673">
                    Прайс-лист Dentico Clinic
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-674">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-675">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-676">
              Дізнайтеся точну вартість вашого лікування
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-677">
              Запишіться на консультацію, щоб отримати індивідуальний план
              лікування та кошторис.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-678">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-679">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-680">
                  <Calendar className="w-5 h-5" data-id="element-681" />
                  Записатись на консультацію
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-682">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-683">
                  <MessageCircle className="w-5 h-5" data-id="element-684" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}